"use client";

/**
 * Layout de documento legal: índice pegado a la izquierda + prosa a la derecha
 * (FEATURE-178).
 *
 * Un texto legal es la única página de la vitrina que se LEE largo, y eso obliga a romper
 * dos costumbres de la landing:
 *
 * - **La medida manda.** La landing usa el ancho completo del `Container`; acá la columna
 *   de texto se corta en ~68 caracteres. Un párrafo legal a 1180 px de ancho no se lee.
 * - **El trazo se retira.** Brochazos y garabatos detrás de un párrafo de doce líneas lo
 *   vuelven ilegible — la misma razón por la que el pincel no entra a la consola. Acá el
 *   trazo queda en la portada y en los separadores, nunca bajo el texto.
 *
 * Es isla cliente solo por el scroll-spy del índice. El índice en sí son `<a href="#id">`
 * nativos: sin JS sigue navegando y lo único que se pierde es el resaltado (CB-005).
 */
import { useEffect, useState } from "react";
import { Container } from "@/components/brand/pincel";
import { LEGAL } from "@/lib/legal";
import type { Frase, Parrafo, Seccion } from "@/content/legal/tipos";

/**
 * Resuelve una frase contra los datos del entorno.
 *
 * Si le falta alguno de los datos que interpola, devuelve `null` y la frase **entera** no
 * se emite: es lo que evita que quede «operado por , RUT , con domicilio en , Chile»
 * (RF-04.1, CB-004).
 */
export function resolverFrase(frase: Frase): string | null {
  if (!frase.necesita?.length) return frase.texto;
  if (frase.necesita.some((dato) => LEGAL[dato] === null)) return null;
  return frase.texto.replace(/\{(\w+)\}/g, (_, clave: string) => LEGAL[clave as keyof typeof LEGAL] ?? "");
}

/** Junta las frases que sobrevivieron. Un párrafo sin ninguna no se emite. */
const resolverFrases = (frases: Frase[]): string =>
  frases.map(resolverFrase).filter((t): t is string => t !== null).join(" ");

function Cuerpo({ p }: { p: Parrafo }) {
  if (p.t === "lista") {
    const items = p.items.map(resolverFrase).filter((t): t is string => t !== null);
    if (!items.length) return null;
    return (
      <ul className="mt-4 space-y-2.5">
        {items.map((item) => (
          <li key={item} className="flex gap-3 text-[15px] leading-relaxed text-ink/80">
            <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }

  const texto = resolverFrases(p.frases);
  if (!texto) return null;

  if (p.t === "nota") {
    return (
      <p className="mt-5 border-l-2 border-accent2/70 bg-panel/60 py-3 pl-4 pr-3 text-[14px] leading-relaxed text-muted">
        {texto}
      </p>
    );
  }
  return <p className="mt-4 text-[15px] leading-relaxed text-ink/80">{texto}</p>;
}

export function Documento({ secciones }: { secciones: Seccion[] }) {
  const [activa, setActiva] = useState(secciones[0]?.id ?? "");

  /* Scroll-spy. Con IntersectionObserver la sección marcada se atrasaba una: la anterior
   * sigue intersectando la banda con su cola, y «la más alta de las visibles» siempre es
   * ella. El criterio correcto es posicional — la ÚLTIMA cuyo título entró al tercio
   * superior de la pantalla, que es donde el ojo está leyendo. */
  useEffect(() => {
    const calcular = () => {
      const umbral = window.innerHeight * 0.35;
      let actual = secciones[0]?.id ?? "";
      for (const s of secciones) {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top <= umbral) actual = s.id;
      }
      setActiva(actual);
    };
    calcular();
    window.addEventListener("scroll", calcular, { passive: true });
    return () => window.removeEventListener("scroll", calcular);
  }, [secciones]);

  return (
    <Container className="grid gap-12 py-12 lg:grid-cols-[240px_minmax(0,1fr)] lg:py-16">
      <nav aria-label="Contenido del documento" className="hidden lg:block">
        <div className="sticky top-24">
          <p className="font-display text-[11px] uppercase tracking-[0.22em] text-ink/60">En este documento</p>
          <ul className="mt-4 space-y-1 border-l border-line">
            {secciones.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className={`-ml-px block border-l-2 py-1.5 pl-4 text-[13px] leading-snug transition ${
                    activa === s.id
                      ? "border-accent font-semibold text-ink"
                      : "border-transparent text-muted hover:border-line hover:text-ink"
                  }`}
                >
                  {s.titulo}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      <article className="max-w-[68ch]">
        {secciones.map((s, i) => (
          <section key={s.id} id={s.id} className={`scroll-mt-24 ${i > 0 ? "mt-12 border-t border-line pt-12" : ""}`}>
            <h2 className="font-display text-[22px] uppercase leading-tight text-ink">{s.titulo}</h2>
            {s.cuerpo.map((p, j) => (
              <Cuerpo key={j} p={p} />
            ))}
          </section>
        ))}
      </article>
    </Container>
  );
}
